<script setup>
import { computed, ref, watch } from "vue"
import { useRouter } from "vue-router"

const props = defineProps({
    modalId: { type: String, required: true },
    issue: { type: Object, default: null },
    retrying: { type: Boolean, default: false },
    resolving: { type: Boolean, default: false },
})

const emit = defineEmits(['retry', 'resolve'])

const router = useRouter()

// Détail technique replié à chaque nouveau cas affiché
const showTechnical = ref(false)
watch(() => props.issue?.id, () => {
    showTechnical.value = false
})

/**
 * Lien vers une page de l'app, ouvert dans un nouvel onglet : la fenêtre reste ouverte pour
 * suivre l'explication.
 */
function appUrl(to) {
    return router.resolve(to).href
}

const typeIcons = {
    email_on_other_contact: 'ri-file-copy-2-line',
    shared_email: 'ri-mail-line',
    sync_failed: 'ri-error-warning-line',
}

/**
 * Découpe un texte d'explication : les repères {client:12}, {request:34} et {contact:56}
 * deviennent des liens vers la fiche client, la demande client ou le contact HubSpot.
 */
function segments(text) {
    const parts = []
    const pattern = /\{(client|request|contact):(\d+)\}/g
    let last = 0
    let match
    while ((match = pattern.exec(text ?? '')) !== null) {
        if (match.index > last) {
            parts.push({ text: text.slice(last, match.index) })
        }
        parts.push({ kind: match[1], id: match[2] })
        last = pattern.lastIndex
    }
    if (last < (text ?? '').length) {
        parts.push({ text: text.slice(last) })
    }
    return parts
}

const linkLabels = {
    client: 'fiche client n° ',
    request: 'demande client n° ',
    contact: 'contact HubSpot n° ',
}

const linkRoutes = {
    client: 'clients.show',
    request: 'client-requests.show',
}

// Icône de chaque étape de l'historique, selon son issue
const stepIcons = {
    success: 'ri-check-line',
    warning: 'ri-alert-line',
    danger: 'ri-close-line',
    info: 'ri-information-line',
}

const icon = computed(() => typeIcons[props.issue?.type] ?? 'ri-information-line')
const isOpen = computed(() => props.issue && !props.issue.resolved_at_fr)
const hasLinks = computed(() => props.issue && (props.issue.contact_url || props.issue.other_contact_url || props.issue.other_client_id || props.issue.client_request_id))
</script>

<template>
    <div :id="props.modalId" class="modal fade" tabindex="-1" :aria-labelledby="props.modalId + 'Title'">
        <div class="modal-dialog modal-lg">
            <div class="modal-content border-0 overflow-hidden" v-if="props.issue">
                <!-- En-tête : type du cas et client -->
                <div class="modal-header p-4" :class="'bg-' + props.issue.type_badge_color + '-subtle'">
                    <div class="d-flex align-items-center">
                        <div class="avatar-sm flex-shrink-0 me-3">
                            <span :class="'avatar-title rounded-circle fs-3 bg-' + props.issue.type_badge_color + ' text-white'">
                                <i :class="icon"></i>
                            </span>
                        </div>
                        <div>
                            <h5 class="modal-title mb-1" :id="props.modalId + 'Title'">{{ props.issue.type_label }}</h5>
                            <p class="text-muted mb-0">
                                {{ props.issue.client.full_name }}
                                <span class="mx-1">•</span>fiche client n° {{ props.issue.client.id }}
                                <span class="mx-1" v-if="props.issue.client.email">•</span>{{ props.issue.client.email }}
                            </p>
                        </div>
                    </div>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>

                <div class="modal-body p-4">
                    <!-- Ce qui se passe : résumé et explication -->
                    <div class="d-flex align-items-start mb-4">
                        <div class="avatar-xs flex-shrink-0 me-3">
                            <span class="avatar-title bg-info-subtle text-info rounded-circle fs-15">
                                <i class="ri-question-line"></i>
                            </span>
                        </div>
                        <div>
                            <h6 class="fs-14 mb-1">Ce qui se passe</h6>
                            <p class="text-dark mb-1">{{ props.issue.summary }}</p>
                            <p class="text-muted mb-0" v-if="props.issue.explanation"><template v-for="(part, partIndex) in segments(props.issue.explanation)" :key="partIndex"><template v-if="part.text">{{ part.text }}</template><a v-else-if="part.kind === 'contact'" :href="props.issue.hubspot_contact_base_url + part.id" target="_blank" rel="noopener" class="link-dark fw-medium">{{ linkLabels.contact + part.id }} <i class="ri-external-link-line align-bottom"></i></a><a v-else :href="appUrl({ name: linkRoutes[part.kind], params: { id: part.id } })" target="_blank" rel="noopener" class="link-dark fw-medium">{{ linkLabels[part.kind] + part.id }} <i class="ri-external-link-line align-bottom"></i></a></template></p>
                        </div>
                    </div>

                    <!-- Ce que la synchro a fait, étape par étape -->
                    <div class="d-flex align-items-start mb-4" v-if="props.issue.history?.length">
                        <div class="avatar-xs flex-shrink-0 me-3">
                            <span class="avatar-title bg-secondary-subtle text-secondary rounded-circle fs-15">
                                <i class="ri-route-line"></i>
                            </span>
                        </div>
                        <div class="flex-grow-1">
                            <h6 class="fs-14 mb-2">Ce que la synchro a fait</h6>
                            <ol class="list-unstyled mb-0 sync-history">
                                <li class="d-flex align-items-start gap-2" v-for="(step, index) in props.issue.history" :key="index">
                                    <span :class="'sync-history-icon flex-shrink-0 rounded-circle d-inline-flex align-items-center justify-content-center bg-' + step.status + '-subtle text-' + step.status">
                                        <i :class="stepIcons[step.status]"></i>
                                    </span>
                                    <span class="text-muted"><template v-for="(part, partIndex) in segments(step.text)" :key="partIndex"><template v-if="part.text">{{ part.text }}</template><a v-else-if="part.kind === 'contact'" :href="props.issue.hubspot_contact_base_url + part.id" target="_blank" rel="noopener" class="link-dark fw-medium">{{ linkLabels.contact + part.id }} <i class="ri-external-link-line align-bottom"></i></a><a v-else :href="appUrl({ name: linkRoutes[part.kind], params: { id: part.id } })" target="_blank" rel="noopener" class="link-dark fw-medium">{{ linkLabels[part.kind] + part.id }} <i class="ri-external-link-line align-bottom"></i></a></template></span>
                                </li>
                            </ol>
                        </div>
                    </div>

                    <!-- Ce qu'il faut faire / traité -->
                    <div class="d-flex align-items-start mb-4" v-if="isOpen">
                        <div class="avatar-xs flex-shrink-0 me-3">
                            <span class="avatar-title bg-warning-subtle text-warning rounded-circle fs-15">
                                <i class="ri-lightbulb-line"></i>
                            </span>
                        </div>
                        <div>
                            <h6 class="fs-14 mb-1">Ce qu'il faut faire</h6>
                            <ul class="text-muted mb-0 ps-3 d-grid gap-1" v-if="props.issue.solutions?.length">
                                <li v-for="(solution, index) in props.issue.solutions" :key="index"><template v-for="(part, partIndex) in segments(solution)" :key="partIndex"><template v-if="part.text">{{ part.text }}</template><a v-else-if="part.kind === 'contact'" :href="props.issue.hubspot_contact_base_url + part.id" target="_blank" rel="noopener" class="link-dark fw-medium">{{ linkLabels.contact + part.id }} <i class="ri-external-link-line align-bottom"></i></a><a v-else :href="appUrl({ name: linkRoutes[part.kind], params: { id: part.id } })" target="_blank" rel="noopener" class="link-dark fw-medium">{{ linkLabels[part.kind] + part.id }} <i class="ri-external-link-line align-bottom"></i></a></template></li>
                            </ul>
                            <p class="text-muted mb-0" v-else>{{ props.issue.how_to_fix }}</p>
                        </div>
                    </div>
                    <div class="d-flex align-items-start mb-4" v-else>
                        <div class="avatar-xs flex-shrink-0 me-3">
                            <span class="avatar-title bg-success-subtle text-success rounded-circle fs-15">
                                <i class="ri-checkbox-circle-line"></i>
                            </span>
                        </div>
                        <div>
                            <h6 class="fs-14 mb-1">Traité</h6>
                            <p class="text-muted mb-0">
                                Le {{ props.issue.resolved_at_fr }}{{ props.issue.resolved_by ? ', par ' + props.issue.resolved_by : ', par une synchro réussie' }}.
                            </p>
                        </div>
                    </div>

                    <!-- Accès directs -->
                    <div class="mb-4" v-if="hasLinks">
                        <h6 class="fs-12 text-uppercase text-muted fw-medium mb-2">Accès directs</h6>
                        <div class="d-flex flex-wrap gap-2">
                            <a :href="props.issue.contact_url" target="_blank" rel="noopener" class="btn btn-soft-dark btn-sm shadow-none d-inline-flex align-items-center gap-1" v-if="props.issue.contact_url">
                                <i class="ri-user-3-line"></i>Contact HubSpot n° {{ props.issue.contact_id }}
                                <i class="ri-external-link-line"></i>
                            </a>
                            <a :href="props.issue.other_contact_url" target="_blank" rel="noopener" class="btn btn-soft-dark btn-sm shadow-none d-inline-flex align-items-center gap-1" v-if="props.issue.other_contact_url">
                                <i class="ri-user-shared-line"></i>Autre contact HubSpot n° {{ props.issue.other_contact_id }}
                                <i class="ri-external-link-line"></i>
                            </a>
                            <a :href="appUrl({ name: 'clients.show', params: { id: props.issue.client.id } })" target="_blank" rel="noopener" class="btn btn-soft-dark btn-sm shadow-none d-inline-flex align-items-center gap-1">
                                <i class="ri-contacts-book-line"></i>Fiche client n° {{ props.issue.client.id }}
                                <i class="ri-external-link-line"></i>
                            </a>
                            <a :href="appUrl({ name: 'clients.show', params: { id: props.issue.other_client_id } })" target="_blank" rel="noopener" class="btn btn-soft-dark btn-sm shadow-none d-inline-flex align-items-center gap-1" v-if="props.issue.other_client_id">
                                <i class="ri-contacts-book-line"></i>Autre fiche client n° {{ props.issue.other_client_id }}
                                <i class="ri-external-link-line"></i>
                            </a>
                            <a :href="appUrl({ name: 'client-requests.show', params: { id: props.issue.client_request_id } })" target="_blank" rel="noopener" class="btn btn-soft-dark btn-sm shadow-none d-inline-flex align-items-center gap-1" v-if="props.issue.client_request_id">
                                <i class="ri-file-list-3-line"></i>Demande client n° {{ props.issue.client_request_id }}
                                <i class="ri-external-link-line"></i>
                            </a>
                        </div>
                    </div>

                    <!-- Historique -->
                    <div class="border rounded mb-4">
                        <div class="row g-0">
                            <div class="col-md-4 border-end">
                                <div class="p-3">
                                    <p class="text-uppercase fw-medium text-muted fs-12 mb-1">Première fois</p>
                                    <h5 class="fs-14 fw-semibold mb-0">{{ props.issue.created_at_fr }}</h5>
                                </div>
                            </div>
                            <div class="col-md-4 border-end">
                                <div class="p-3">
                                    <p class="text-uppercase fw-medium text-muted fs-12 mb-1">Dernière fois</p>
                                    <h5 class="fs-14 fw-semibold mb-0">{{ props.issue.last_seen_at_fr }}</h5>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="p-3">
                                    <p class="text-uppercase fw-medium text-muted fs-12 mb-1">Nombre de fois</p>
                                    <h5 class="fs-14 fw-semibold mb-0">{{ props.issue.occurrences }}</h5>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Détail technique, replié -->
                    <button type="button" class="btn btn-soft-dark btn-sm shadow-none d-inline-flex align-items-center gap-1" :aria-expanded="showTechnical" @click="showTechnical = !showTechnical">
                        <i class="ri-code-s-slash-line"></i>{{ showTechnical ? 'Masquer' : 'Afficher' }} le détail technique
                        <i :class="showTechnical ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                    </button>
                    <div class="mt-2" v-show="showTechnical">
                        <div class="bg-light rounded p-3 fs-12 text-break">
                            <div>{{ props.issue.reason }}</div>
                            <div class="text-danger mt-2" v-if="props.issue.error">{{ props.issue.error }}</div>
                            <div class="text-muted mt-2" v-if="props.issue.contact_id || props.issue.other_contact_id">
                                Contacts HubSpot : {{ [props.issue.contact_id, props.issue.other_contact_id].filter(Boolean).join(', ') }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal-footer border-top p-3">
                    <button type="button" class="btn btn-soft-dark fw-medium text-decoration-none shadow-none" data-bs-dismiss="modal">Fermer</button>
                    <template v-if="isOpen && props.issue.can_update">
                        <button type="button" class="btn btn-success" :disabled="props.resolving" @click="emit('resolve', props.issue.id)">
                            <i class="ri-checkbox-circle-line align-bottom me-1"></i>Marquer comme traité
                        </button>
                        <button type="button" class="btn btn-secondary" :disabled="props.retrying" @click="emit('retry', props.issue.id)">
                            <span v-if="props.retrying" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            <i class="ri-refresh-line align-bottom me-1" v-else></i>Relancer la synchro
                        </button>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.sync-history {
    display: grid;
    gap: 0.5rem;
}

.sync-history-icon {
    width: 1.375rem;
    height: 1.375rem;
    font-size: 0.8125rem;
    margin-top: 0.0625rem;
}
</style>
